import { InfoCircleOutlined } from '@ant-design/icons';
import { Radio } from 'antd';
import type { Chart, ChartOptions } from 'chart.js';
import commaNumber from 'comma-number';
import { useCallback, useMemo, useRef } from 'react';
import { Line } from 'react-chartjs-2';
import styled from 'styled-components';

import AIIcon from '@/components/Badge/AIBadge/AIIcon';
import { Flex, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import IconButton from '@/components/Button/IconButton';
import TooltipV2 from '@/components/Tooltip/TooltipV2';
import Heading from '@/components/Typography/Heading';
import Typo, { Paragraph } from '@/components/Typography/Typo';
import AMP_KEYS from '@/configs/amp-track-keys';
import { createTimeSeriesPeakPinPlugin } from '@/features/(charts)/chart-renderer/lib/time-series-peak-pin-plugin';
import {
  getRenderedTimeSeriesPeakPointCandidates,
  pickTimeSeriesPeakPoints,
} from '@/features/(charts)/chart-renderer/lib/time-series-peak-point';
import { CollapseState } from '@/features/AskSyncly/types/constants';
import useInsightExplorerFeedbackListModal from '@/features/InsightExplorer/hooks/useInsightExplorerFeedbackListModal';
import { buildInsightExplorerPeakAnalysisOptions } from '@/features/InsightExplorer/lib/build-insight-explorer-peak-analysis-options';
import {
  type InsightExplorerFieldType,
  InsightExplorerFieldTypeMap,
  InsightExplorerTrendViewType,
  InsightExplorerTrendViewTypeMap,
  InsightExplorerViewType,
} from '@/features/InsightExplorer/types/constants';
import type { InsightExplorerTrendResponse } from '@/features/InsightExplorer/types/types';
import { insightExplorerUtils } from '@/features/InsightExplorer/utils/insight-explorer.utils';
import useColor from '@/hooks/domain/useColor';
import type { RangeUnit } from '@/utils/getDatePreset';
import { parseDateFormatByRangeUnit } from '@/utils/time';
import { track } from '@/utils/track/track';

import type { HierarchyNodeData } from '../../InsightExplorerSidebar/HierarchyNavigator/HierarchyNavigator';
import ExplorerChartCustomTooltip from '../ExplorerChartCustomTooltip/ExplorerChartCustomTooltip';
import { useExplorerChartTooltip } from '../ExplorerChartCustomTooltip/useExplorerChartTooltip';
import { useTotalVolumeTrendSection } from './useTotalVolumeTrendSection';

const TotalVolumeTrendSection = (props: {
  trendViewType: InsightExplorerTrendViewType;
  data: InsightExplorerTrendResponse;
  field: InsightExplorerFieldType;
  view: InsightExplorerViewType;
  viewType: RangeUnit;
  filterPayload: StructuredFilter;
  selectedNode: HierarchyNodeData;
  findNode: (alias: string) => HierarchyNodeData | null;
  onTrendViewTypeChange: (trendViewType: InsightExplorerTrendViewType) => void;
}) => {
  const openInsightExplorerFeedbackListModal =
    useInsightExplorerFeedbackListModal();
  const colors = useColor();
  const chartRef = useRef<Chart<'line'>>(null);

  const { chartData, heroDisplayValue, heroDisplaylabel } =
    useTotalVolumeTrendSection({
      trendViewType: props.trendViewType,
      data: props.data,
      view: props.view,
      viewType: props.viewType,
      selectedNode: props.selectedNode,
    });

  const { tooltipData, chartTooltip, hideTooltip } = useExplorerChartTooltip({
    chart: chartRef.current,
  });

  const chartOptions: ChartOptions<'line'> = useMemo(
    () => ({
      events: ['mouseleave', 'mousemove', 'click'],
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        eventPlugin: {
          events: ['mouseleave', 'click'],
        },
        datalabels: {
          display: false,
        },
        legend: {
          display: false,
        },
        tooltip: {
          enabled: false,
          external: chartTooltip,
        },
      },
      scales: {
        x: {
          display: true,
          grid: {
            color: colors.gray250,
            drawTicks: false,
          },
          ticks: {
            color: colors.gray500,
            font: {
              size: 11,
              family: 'Instrument Sans',
            },
            maxRotation: 45,
          },
        },
        y: {
          display: true,
          grace: '10%',
          grid: {
            color: colors.gray250,
            drawTicks: false,
          },
          ticks: {
            color: colors.gray500,
            font: {
              size: 11,
              family: 'Instrument Sans',
            },
          },
          beginAtZero: true,
        },
      },
      elements: {
        line: {
          borderWidth: 2,
        },
        point: {
          hoverRadius: 6,
        },
      },
    }),
    [chartTooltip, colors.gray250, colors.gray500],
  );

  const openFeedbackModal = (expandAI?: boolean) => {
    if (expandAI) {
      track(AMP_KEYS.INSIGHT_EXPLORER_ASK_SYNCLY_CLICK, {
        node_type: InsightExplorerFieldTypeMap.get(props.field),
        node_name: props.selectedNode.name,
      });
    } else {
      track(AMP_KEYS.INSIGHT_EXPLORER_FEEDBACK_LIST_CLICK, {
        node_type: InsightExplorerFieldTypeMap.get(props.field),
        node_name: props.selectedNode.name,
      });
    }
    if (!props.selectedNode) return;

    const filter = insightExplorerUtils
      .getFilterByExplorerOptions({
        trendViewType: props.trendViewType,
        view: props.view,
        filterPayload: props.filterPayload,
        selectedNode: props.selectedNode,
      })
      .get();

    openInsightExplorerFeedbackListModal({
      filter,
      socketInfo: {
        initialState: expandAI
          ? CollapseState.EXPANDED
          : CollapseState.COLLAPSED,
      },
    });
  };

  const openPeakFeedbackModal = useCallback(
    (dataLabel: string) => {
      if (!props.selectedNode) return;

      const dateRange = parseDateFormatByRangeUnit(dataLabel, props.viewType);
      const sFilter = insightExplorerUtils.getFilterByExplorerOptions({
        trendViewType: props.trendViewType,
        view: props.view,
        filterPayload: props.filterPayload,
        selectedNode: props.selectedNode,
      });

      sFilter.setDateFilter({
        start_date: dateRange.start,
        end_date: dateRange.end,
      });

      const filter = sFilter.get();

      openInsightExplorerFeedbackListModal({
        filter,
        peakAnalysis: buildInsightExplorerPeakAnalysisOptions({
          autoStart: true,
          filter,
          viewType: props.viewType,
          chartName: `${props.selectedNode.name} trend`,
        }),
      });
    },
    [
      openInsightExplorerFeedbackListModal,
      props.filterPayload,
      props.selectedNode,
      props.trendViewType,
      props.view,
      props.viewType,
    ],
  );

  const peakPinPlugin = useMemo(
    () =>
      createTimeSeriesPeakPinPlugin({
        resolvePeakPoints: (chart) =>
          pickTimeSeriesPeakPoints(
            getRenderedTimeSeriesPeakPointCandidates(chart),
          ),
        onClick: ({ dataLabel }) => openPeakFeedbackModal(dataLabel),
      }),
    [openPeakFeedbackModal],
  );
  // react-chartjs-2 only applies plugins when the chart instance is created.
  const chartInstanceKey = useMemo(
    () =>
      JSON.stringify({
        filterPayload: props.filterPayload,
        selectedNodeId: props.selectedNode.id,
        selectedNodeName: props.selectedNode.name,
        trendViewType: props.trendViewType,
        view: props.view,
        viewType: props.viewType,
      }),
    [
      props.filterPayload,
      props.selectedNode.id,
      props.selectedNode.name,
      props.trendViewType,
      props.view,
      props.viewType,
    ],
  );

  return (
    <VFlex g={24}>
      <Flex j="space-between" a="center" style={{ height: 32 }}>
        <Typo type="label1" weight="sb" color={colors.gray800}>
          Total
        </Typo>
        {props.view === InsightExplorerViewType.SENTIMENTS && (
          <Radio.Group value={props.trendViewType}>
            {InsightExplorerTrendViewTypeMap.toArray().map((op) => {
              return (
                <TooltipV2
                  key={op.value}
                  tooltipNode={
                    op.value ===
                    InsightExplorerTrendViewType.SENTIMENT_SCORE ? (
                      <>
                        A higher score indicates a larger share of positive
                        feedback.
                      </>
                    ) : null
                  }
                >
                  <Radio.Button
                    value={op.value}
                    onChange={() => {
                      props.onTrendViewTypeChange(op.value);
                      track(AMP_KEYS.INSIGHT_EXPLORER_TREND_VIEW_SELECT, {
                        trend_view: op.label,
                      });
                    }}
                  >
                    {op.label}
                  </Radio.Button>
                </TooltipV2>
              );
            })}
          </Radio.Group>
        )}
      </Flex>

      <Flex g={24}>
        <ChartWrapper onMouseLeave={hideTooltip}>
          {chartData && (
            <Line
              key={chartInstanceKey}
              data={chartData}
              options={chartOptions}
              plugins={[peakPinPlugin]}
              ref={chartRef}
            />
          )}
          {tooltipData && (
            <ExplorerChartCustomTooltip
              tooltip={tooltipData}
              from="total"
              meta={{
                field: props.field,
                selectedNode: props.selectedNode,
                view: props.view,
                viewType: props.viewType,
                filterPayload: props.filterPayload,
                findNode: props.findNode,
                trendViewType: props.trendViewType,
              }}
              hideTooltip={hideTooltip}
            />
          )}
        </ChartWrapper>
        <StatCard>
          <VFlex g={16} a="center">
            <VFlex g={6} a="center">
              <Typo type="body3" weight="m" color={colors.gray500}>
                {heroDisplaylabel}
                {props.view === InsightExplorerViewType.SENTIMENTS &&
                  props.trendViewType ===
                    InsightExplorerTrendViewType.SENTIMENT_SCORE && (
                    <TooltipV2
                      tooltipNode={
                        <>
                          <Paragraph type="body3">
                            (Total Feedback - Negative Feedback) ÷ Total
                            Feedback x 100.
                          </Paragraph>
                        </>
                      }
                    >
                      <InfoCircleOutlined style={{ marginLeft: 3 }} />
                    </TooltipV2>
                  )}
              </Typo>
              <Heading type="h1" weight="sb" color={colors.gray800}>
                {commaNumber(heroDisplayValue)}
              </Heading>
            </VFlex>
            <Flex g={6}>
              <Button
                variant="outlined"
                onClick={() => openFeedbackModal(false)}
              >
                View data
              </Button>
              <IconButton
                icon={<AIIcon />}
                onClick={() => openFeedbackModal(true)}
              />
            </Flex>
          </VFlex>
        </StatCard>
      </Flex>
    </VFlex>
  );
};

const ChartWrapper = styled.div`
  position: relative;
  flex: 1;
  height: 280px;
  min-width: 400px;
`;

const StatCard = styled.div`
  width: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 0 80px;
`;

export default TotalVolumeTrendSection;
